import * as React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import isEqual from 'lodash.isequal'
import diff from 'lodash.difference'
import { object, string, number, array, boolean } from 'yup'

import { createContext } from '@clara/react-utils'
import { FileObject } from '@clara/file-viewer'
import { useAsyncFn, useControllableState, usePrevious } from '@clara/hooks'
import { deleteAt } from '@/utils/array'
import { noop, omit } from '@clara/utils'
import { useLocaleContext } from '@clara/locale'
import { useSnackbar } from '@clara/ui'

import { Attachment } from '@/types/api'
import {
  ReimbursementValues,
  createReimbursement,
} from '../api/create-reimbursement'
import { createAttachment } from '../api/create-attachment'
import { useMetadata, UseMetadataReturn } from '../hooks/use-metadata'
import {
  UseReimbursementReturn,
  useReimbursement,
} from '../hooks/use-reimbursement'
import { updateReimbursement } from '../api/update-reimbursement'
import { deleteAttachment } from '../api/delete-attachment'
import { toNumber } from '@/utils/number'
import { updateLabels } from '../api/update-labels'

type ActionProp = 'resubmit' | null
/**
 * Options for managing the creation or update of a reimbursement.
 */
type ManageReimbursementOptions = {
  /**
   * The metadata used to render information related to the reimbursement.
   * This contains additional details like the status, title, or any other
   * relevant information that needs to be displayed in the UI.
   */
  metadata: UseMetadataReturn

  /**
   * The reimbursement data including the id, amount, and other fields
   * that pertain to the specific reimbursement being created or updated.
   * It handles all the logic and state related to the reimbursement itself.
   */
  reimbursement: UseReimbursementReturn

  /**
   * Specifies the current strategy for handling the reimbursement.
   * If 'create', a new reimbursement is being added; if 'update',
   * an existing reimbursement is being modified.
   *
   * @default 'create'
   */
  strategy?: 'update' | 'create'

  /**
   * The maximum allowed size for attachments, typically measured in bytes.
   * This determines the file size limit for any document or image that
   * can be uploaded as part of the reimbursement process.
   *
   * Example: `10485760` for 10MB.
   */
  attachmentMaxSize?: number

  /**
   * The maximum number of files that can be attached to the reimbursement.
   * This restricts the total count of documents or images that can be
   * submitted during the reimbursement creation or update.
   *
   * Example: `5` to limit the number of attachments to 5 files.
   */
  attachmentMaxFiles?: number

  /**
   * Controls the reimbursement submission behavior.
   * When set to 'resubmit', forces creation of a new reimbursement (POST request)
   * and re-uploads all attachments, regardless of the current strategy.
   * This is typically used when resubmitting a rejected reimbursement.
   * When null, follows the default behavior defined by the strategy prop.
   *
   * @default null
   */
  action: ActionProp
}

function checkStore(
  storeValues: Partial<ReimbursementValues>,
): storeValues is ReimbursementValues {
  const shape = object().shape({
    amount: number().positive().min(0.1).required(),
    category: string().required(),
    expenseDate: string().required(),
    description: string().min(0).max(200),
    merchant: string().required(),
    userBank: string().required(),
    labels: array()
      .of(
        object().shape({
          labelUUID: string(),
          labelName: string(),
          canDelete: boolean().optional(),
        }),
      )
      .required(),
  })
  const isValid = shape.isValidSync(storeValues)

  return isValid
}

type DraftValues = Partial<ReimbursementValues>

type SubmissionState = 'idle' | 'submitted'

function useManageStore({
  metadata,
  strategy = 'create',
  reimbursement,
  attachmentMaxSize: maxSize = 10 * 1024 * 1024,
  attachmentMaxFiles: maxFiles = 7,
  action,
}: ManageReimbursementOptions) {
  const [attachments, setAttachments] = React.useState<FileObject[]>([])

  const [values, setValues] = useControllableState<DraftValues>({
    defaultValue: {},
    onChange: noop,
    shouldUpdate: (prev, next) => isEqual(prev, next) === false,
  })

  const toast = useSnackbar()

  const hasAttachments = Array.isArray(attachments) && attachments.length > 0
  const [submissionState, setSubmissionState] =
    React.useState<SubmissionState>('idle')

  const { uuid: uid } = reimbursement.value ?? {}
  const navigate = useNavigate()
  const {
    defaultValues,
    defaultAttachments,
    attachments: initialAttachments,
  } = reimbursement

  const metadataValues = React.useMemo(
    () => ({
      userBank: metadata.userbank?.uuid ?? '',
      countryCode: metadata.options.country_code,
      currencyCode: metadata.options.currency_code,
    }),
    [metadata],
  )

  const storeValues = React.useMemo(
    () => ({
      ...metadataValues,
      ...values,
    }),
    [values, metadataValues],
  )
  const prevStoreValues = usePrevious(storeValues)

  const [createState, create] = useAsyncFn(
    async (payload: ReimbursementValues, attachments: FileObject[]) => {
      setSubmissionState('submitted')

      const uploaded = await Promise.all(
        attachments.map(async (attachment) => {
          return createAttachment(attachment)
        }),
      )

      const response = await createReimbursement({
        ...payload,
        labels:
          payload.labels.map((label) => ({ labelUUID: label.labelUUID })) ?? [],
        attachments: uploaded,
      })

      return response
    },
  )

  const [updateState, update] = useAsyncFn(
    async (
      payload: Partial<ReimbursementValues>,
      attachments: FileObject[],
    ) => {
      setSubmissionState('submitted')
      const cleaned = omit(payload, ['labels'])
      // diff
      const dropped = diff(defaultAttachments, attachments)
      const added = diff(attachments, defaultAttachments)
      const uploaded = await Promise.all(
        added.map(async (attachment) => {
          return createAttachment(attachment)
        }),
      )
      const _attachments = initialAttachments
        .filter(
          (attachment) =>
            !dropped.some((drppd) => drppd.uri === attachment.link),
        )
        .concat(uploaded)

      const response = await updateReimbursement([
        {
          ...cleaned,
          uuid: uid,
          attachments: _attachments,
        },
      ])

      if (uid) {
        try {
          await updateLabels(
            uid,
            (payload.labels ?? []).map((label) => ({
              labelUUID: label.labelUUID,
            })),
          )
        } catch (error) {
          console.error('labels couldnt be updated', error)
        }
      }

      return response
    },
    [setSubmissionState, uid, defaultAttachments],
  )

  // create loading state
  const loadingState =
    updateState.loading || createState.loading
      ? 'processing'
      : updateState.error || createState.error
        ? submissionState === 'idle'
          ? 'idle'
          : 'error'
        : submissionState === 'submitted'
          ? 'success'
          : 'idle'

  const dropAttachmentAt = React.useCallback(
    (index: number, onSuccess?: () => void, onError?: () => void) => {
      const handleSuccess = () => {
        setAttachments((prev) => deleteAt(index, prev))
        onSuccess?.()
      }

      const handleError = () => onError?.()
      // toast({ status: 'error', message: 'failed to drop' })

      const dropFromApi = (attachment: Attachment) =>
        deleteAttachment(attachment.documentId)
          .then(handleSuccess)
          .catch(handleError)

      strategy === 'create' ||
      action === 'resubmit' ||
      (strategy === 'update' && !initialAttachments[index]?.documentId)
        ? handleSuccess()
        : dropFromApi(initialAttachments[index])
    },
    [strategy, action, initialAttachments],
  )

  React.useEffect(() => {
    if (strategy === 'create') return

    if (Array.isArray(defaultAttachments) && defaultAttachments.length > 0)
      setAttachments(defaultAttachments)
  }, [defaultAttachments, strategy])

  React.useEffect(() => {
    // should update
    if (isEqual(prevStoreValues, storeValues)) return

    // not valid store
    if (!(hasAttachments && checkStore(storeValues))) return

    if (strategy === 'create' || action === 'resubmit') {
      create(storeValues, attachments)
    } else if (strategy === 'update') update(storeValues, attachments)
  })

  React.useEffect(() => {
    if (loadingState === 'idle') return

    const action = [
      `/reimbursements/${strategy}/`,
      strategy === 'create' ? loadingState : `${uid}/${loadingState}`,
    ].join('')

    navigate(action)
  }, [loadingState, navigate, strategy, uid])

  const resetStore = React.useCallback(() => {
    setSubmissionState('idle')

    setValues(metadataValues)
    setAttachments([])
  }, [setSubmissionState, metadataValues, setValues])

  return {
    attachments,
    setAttachments,
    dropAttachmentAt,
    hasAttachments,
    values: storeValues,
    updateAmount: (amount?: number | string) => {
      const numericAmount = toNumber(amount)

      setValues((prev) => ({
        ...prev,
        amount: numericAmount,
      }))
    },
    updateValues: (next: DraftValues) => {
      setValues((prev) => ({
        ...prev,
        ...next,
      }))
    },
    resetStore,
    loadingState,
    error: createState.error,
    isLoading: loadingState === 'processing',
    metadata,
    reimbursement,
    defaultValues,
    defaultAttachments,
    strategy,
    toast,
    uid,
    maxSize,
    maxFiles,
    action,
  }
}

type ManageStore = ReturnType<typeof useManageStore>

const [CreateReimbursementContextProvider, useManageReimbursement] =
  createContext<ManageStore>({
    name: 'CreateReimbursementStore',
    strict: true,
  })

export { useManageReimbursement }

export interface ManageReimbursementProps extends ManageReimbursementOptions {}

function Store(props: React.PropsWithChildren<ManageReimbursementProps>) {
  const _store = useManageStore(props)
  const store = React.useMemo(() => _store, [_store])

  return (
    <CreateReimbursementContextProvider value={store}>
      {props.children}
    </CreateReimbursementContextProvider>
  )
}

export function ManageReimbursementStore(props: React.PropsWithChildren<{}>) {
  const { locale } = useLocaleContext()
  const options = React.useMemo(() => {
    const lang = locale.i18n.language.slice(0, 2).toUpperCase() ?? 'ES'
    return {
      lang,
      country_code: locale.preferences.id,
      currency_code: locale.preferences.currency.code,
    }
  }, [locale])
  const metadata = useMetadata(options)

  const { uid } = useParams()
  const [searchParams] = useSearchParams()
  const action = searchParams.get('action') as ActionProp
  const reimbursement = useReimbursement({
    uid,
  })
  const strategy = typeof uid === 'string' ? 'update' : 'create'

  return (
    <Store
      {...props}
      strategy={strategy}
      reimbursement={reimbursement}
      metadata={metadata}
      action={action}
    >
      {props.children}
    </Store>
  )
}
